import {Box, Chip, FormControlLabel, Switch, Tooltip} from '@mui/material';
import {useCallback, useEffect, useRef, useState} from 'react';

import {OcclusionState, OverlayFrame, OverlaySidecar} from '../../../interfaces/HazardAILog';
import {renderOverlay} from './overlay/renderer';

interface Props {
  videoUrl: string;
  overlay: OverlaySidecar;
}

/** Human-readable label and MUI colour for each non-clear occlusion state. */
const OCCLUSION_DISPLAY: Partial<Record<OcclusionState, { label: string; color: 'warning' | 'error' }>> = {
  partial: { label: 'Partial occlusion', color: 'warning' },
  blocked: { label: 'Camera blocked',    color: 'error' },
  overexposed: { label: 'Overexposed',       color: 'warning' },
  foggy: { label: 'Foggy / blurry',    color: 'warning' },
};

/** Find the overlay frame closest to t (seconds), within a 150 ms window. */
const findFrame = (frames: OverlayFrame[], t: number): OverlayFrame | null => {
  let best: OverlayFrame | null = null;
  let bestDiff = 0.15;
  for (const frame of frames) {
    const diff = Math.abs(frame.t - t);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = frame;
    }
  }
  return best;
};

export const HazardVideoOverlayPlayer = ({videoUrl, overlay}: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [showOverlay, setShowOverlay] = useState(true);
  const [opaqueMode, setOpaqueMode] = useState(false);
  const [showMasks, setShowMasks] = useState(true);
  const [occlusion, setOcclusion] = useState<OcclusionState | undefined>();

  const draw = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
 return;
}

    // Match canvas to the video element's full rendered box (including any letterbox bars).
    const {clientWidth, clientHeight, videoWidth, videoHeight} = video;
    if (canvas.width !== clientWidth) {
 canvas.width = clientWidth;
}
    if (canvas.height !== clientHeight) {
 canvas.height = clientHeight;
}

    const ctx = canvas.getContext('2d');
    if (!ctx) {
 return;
}
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!showOverlay || canvas.width === 0 || canvas.height === 0) {
 return;
}

    const frame = findFrame(overlay.frames, video.currentTime);

    // Update occlusion badge state only on change to avoid a setState per rendered frame
    const frameOcclusion = frame?.occlusion;
    setOcclusion(prev => prev === frameOcclusion ? prev : frameOcclusion);

    if (!frame) {
 return;
}

    // Wait until the browser has decoded the intrinsic video dimensions — without them
    // we cannot compute the letterbox offset and would misplace the overlay.
    if (!videoWidth || !videoHeight) {
 return;
}

    // object-fit: contain scales the video uniformly to fit the element box, centred.
    // The rendered content area is smaller than the element box when the aspect ratios
    // differ, producing black bars on two sides. Compute that inset so overlays are
    // placed relative to the actual content rather than the element edges.
    const fit = Math.min(clientWidth / videoWidth, clientHeight / videoHeight);
    const renderedW = videoWidth * fit;
    const renderedH = videoHeight * fit;
    const offsetX = (clientWidth - renderedW) / 2;
    const offsetY = (clientHeight - renderedH) / 2;

    const scaleX = renderedW / overlay.sourceWidth;
    const scaleY = renderedH / overlay.sourceHeight;
    renderOverlay(ctx, frame, scaleX, scaleY, {
      maskAlpha: opaqueMode ? 1.0 : 0.5,
      showMasks,
      showBoxes: true,
      offsetX,
      offsetY,
    });
  }, [overlay, showOverlay, opaqueMode, showMasks]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
 return;
}

    // requestVideoFrameCallback fires once per rendered frame — ideal for overlay sync.
    // Fall back to timeupdate + rAF for browsers that don't support it yet.
    if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
      let id: number;
      const rvfc = () => {
        draw();
        id = (video as any).requestVideoFrameCallback(rvfc);
      };
      id = (video as any).requestVideoFrameCallback(rvfc);
      return () => (video as any).cancelVideoFrameCallback(id);
    } else {
      const onTimeUpdate = () => requestAnimationFrame(draw);
      video.addEventListener('timeupdate', onTimeUpdate);
      return () => video.removeEventListener('timeupdate', onTimeUpdate);
    }
  }, [draw]);

  return (
    <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
      {/* Controls bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          backgroundColor: 'rgba(0,0,0,0.6)',
          flexShrink: 0,
        }}
      >
        <Tooltip title="Show/hide detection overlay">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showOverlay}
                onChange={(e) => setShowOverlay(e.target.checked)}
                sx={{'& .MuiSwitch-thumb': {backgroundColor: 'rgb(255,0,255)'}}}
              />
            }
            label={
              <Box component="span" sx={{color: 'white', fontSize: 12}}>
                Overlay
              </Box>
            }
            sx={{m: 0}}
          />
        </Tooltip>
        <Tooltip title="Toggle opaque / translucent mask">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={opaqueMode}
                onChange={(e) => setOpaqueMode(e.target.checked)}
                disabled={!showOverlay}
              />
            }
            label={
              <Box component="span" sx={{color: 'white', fontSize: 12}}>
                Solid mask
              </Box>
            }
            sx={{m: 0}}
          />
        </Tooltip>
        <Tooltip title="Show/hide instance masks">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showMasks}
                onChange={(e) => setShowMasks(e.target.checked)}
                disabled={!showOverlay}
              />
            }
            label={
              <Box component="span" sx={{color: 'white', fontSize: 12}}>
                Masks
              </Box>
            }
            sx={{m: 0}}
          />
        </Tooltip>

        {/* Occlusion badge — visible only when overlay is on and state is non-clear */}
        {showOverlay && occlusion && OCCLUSION_DISPLAY[occlusion] && (
          <Chip
            size="small"
            label={OCCLUSION_DISPLAY[occlusion]!.label}
            color={OCCLUSION_DISPLAY[occlusion]!.color}
            sx={{ml: 'auto', fontSize: 11, height: 20}}
          />
        )}
      </Box>

      {/* Video + canvas stack */}
      <Box sx={{position: 'relative', flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          style={{width: '100%', height: '100%', objectFit: 'contain', display: 'block'}}
        />
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Box>
  );
};

export default HazardVideoOverlayPlayer;
